import React from 'react';
import { getWindDirection } from '../../utils/weatherUtils';
import Icon from '../../utils/icons';
import './MetricsGrid.css';

// AQI color/status mapping (OWM uses 1-5 scale)
const getAqiStatus = (aqi) => {
  if (aqi <= 1) return 'good';
  if (aqi <= 2) return 'normal';
  if (aqi <= 3) return 'warning';
  return 'critical';
};

const getAqiColor = (aqi) => {
  const colors = { 1: '#4caf50', 2: '#8bc34a', 3: '#ffc107', 4: '#ff9800', 5: '#f44336' };
  return colors[aqi] || '#999';
};

function MetricsGrid({ data, aqi }) {
  if (!data) return null;

  const { current } = data;

  const metrics = [
    { 
      icon: 'droplet', 
      label: 'Humidity', 
      value: `${current?.humidity || 0}%`,
      status: current?.humidity > 70 ? 'warning' : 'normal'
    },
    { 
      icon: 'wind', 
      label: 'Wind Speed', 
      value: `${current?.wind_speed || 0} mph`,
      status: current?.wind_speed > 25 ? 'warning' : 'normal'
    },
    { 
      icon: 'gauge', 
      label: 'Pressure', 
      value: `${current?.pressure || 0} hPa`,
      status: 'normal'
    },
    { 
      icon: 'eye', 
      label: 'Visibility', 
      value: `${((current?.visibility || 0) / 1000).toFixed(1)} km`,
      status: current?.visibility < 5000 ? 'warning' : 'normal'
    },
    { 
      icon: 'cloud', 
      label: 'Cloud Cover', 
      value: `${current?.clouds || 0}%`,
      status: 'normal'
    },
  ];

  // Add AQI metric if available
  if (aqi) {
    metrics.push({
      icon: 'leaf',
      label: 'Air Quality',
      value: aqi.label,
      subValue: `AQI ${aqi.aqi}/5`,
      status: getAqiStatus(aqi.aqi),
      color: getAqiColor(aqi.aqi),
      tooltip: aqi.components ? `PM2.5: ${aqi.components.pm2_5?.toFixed(1) || '—'} · O₃: ${aqi.components.o3?.toFixed(1) || '—'} µg/m³` : null,
    });
  } else {
    metrics.push({
      icon: 'compass', 
      label: 'Wind Dir', 
      value: getWindDirection(current?.wind_direction),
      status: 'normal'
    });
  }

  return (
    <div className="metrics-grid">
      <div className="metrics-header">
        <h2 className="card-title">
          <Icon name="activity" size={18} /> Weather Metrics
        </h2>
        <span className="metrics-subtitle">Real-time monitoring</span>
      </div>

      <div className="metrics-container">
        {metrics.map((metric, index) => (
          <div key={index} className={`metric-card ${metric.status}`} title={metric.tooltip || ''}>
            <div className="metric-icon-wrapper" style={metric.color ? { color: metric.color } : {}}>
              <Icon name={metric.icon} size={20} />
            </div>
            <div className="metric-info">
              <span className="metric-label">{metric.label}</span>
              <span className="metric-value" style={metric.color ? { color: metric.color } : {}}>
                {metric.value}
              </span>
              {metric.subValue && (
                <span className="metric-sub-value">{metric.subValue}</span>
              )}
            </div>
            <div className={`metric-status-indicator ${metric.status}`}></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MetricsGrid;
